import { z } from 'zod';
import { inboxClarificationCompleteBodySchema } from '../sdd-spec-pipeline/clarification-resume.schemas.js';
import { workItemReadinessSnippetSchema } from '../sdd-implementation-readiness/work-item-readiness-snippet.schema.js';
/** DC-345 — persisted `platform.inbox_items.category` for scripted stage handoffs (varchar(64)). */
export const INBOX_ITEM_CATEGORY_DEMO_STAGE_HANDOFF = 'demo_stage_handoff';
/** DC-410 — seeding-only inbox rows (superseded on re-seed; distinct from webhook handoffs). */
export const INBOX_ITEM_CATEGORY_DEMO_SCENARIO_SEED = 'demo_scenario_seed';
/** DC-419 — repository scope confirmation pause (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_REPOSITORY_SCOPE_CONFIRMATION = 'repository_scope_confirmation';
/** DC-437 — SDD / MCP anomaly operator inbox routing (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_SDD_ANOMALY = 'sdd_anomaly';
/** DC-448 — artifact discussion routing escalation (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_ARTIFACT_COMMENT_NEEDS_HUMAN = 'artifact_comment_needs_human';
/** DC-388 — governance standards routing approvals (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_GOVERNANCE_ROUTING = 'governance_routing';
/** Access-request approvals routed to org admins. */
export const INBOX_ITEM_CATEGORY_ORGANIZATION_ACCESS_REQUEST = 'organization_access_request';
/** Stakeholder notifications — spec draft ready, routed to the ticket's reporter/PM (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_SPEC_REVIEW_READY = 'spec_review_ready';
/** Stakeholder notifications — tech-plan/tasks/tests draft ready, routed to the ticket's assignee (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_IMPLEMENTATION_ARTIFACTS_READY = 'implementation_artifacts_ready';
/** Stakeholder notifications — reassignment onto a ticket with implementation artifacts already generated, routed to the new assignee (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_ASSIGNEE_ARTIFACTS_HANDOFF = 'assignee_artifacts_handoff';
/** Stakeholder notifications — ticket title/description changed, routed to the current reporter/PM and assignee (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_WORK_ITEM_CONTENT_UPDATED = 'work_item_content_updated';
/**
 * Stakeholder notifications — assigned a work item that has no implementation
 * artifacts yet, routed to the new assignee (varchar(64) on
 * `platform.inbox_items.category`).
 *
 * Distinct from {@link INBOX_ITEM_CATEGORY_ASSIGNEE_ARTIFACTS_HANDOFF}, which keeps its
 * narrower meaning: assigned *and* there is already something generated to review.
 */
export const INBOX_ITEM_CATEGORY_WORK_ITEM_ASSIGNED = 'work_item_assigned';
/** Stakeholder notifications — a work item moved off the recipient, routed to the outgoing assignee (varchar(64) on `platform.inbox_items.category`). */
export const INBOX_ITEM_CATEGORY_WORK_ITEM_UNASSIGNED = 'work_item_unassigned';
/**
 * The ticket description changed after the specification was approved, so CodeMerlin
 * held regeneration rather than replacing approved work, and is asking the reporter and
 * assignee to decide (varchar(64) on `platform.inbox_items.category`).
 *
 * Replaces {@link INBOX_ITEM_CATEGORY_WORK_ITEM_CONTENT_UPDATED} for this case rather
 * than stacking on top of it — the generic "ticket updated" row is suppressed when an
 * approved spec exists, so one edit produces one row.
 */
export const INBOX_ITEM_CATEGORY_SPEC_REGENERATION_WITHHELD = 'spec_regeneration_withheld';
/**
 * Approval gap-fill stopped: automatic regeneration of a missing downstream artifact was
 * tried the maximum number of times for the same upstream version, so a member has to
 * retry by hand (varchar(64) on `platform.inbox_items.category`).
 */
export const INBOX_ITEM_CATEGORY_SDD_GAP_FILL_RETRY_CAP = 'sdd_gap_fill_retry_cap';
/**
 * Origin prefix shared by every gap-fill retry-cap row for one work item and downstream
 * profile; the upstream version id follows it. Lets the generation side retire the rows
 * once that artifact finally exists.
 */
export function buildGapFillRetryCapOriginEventIdPrefix(params) {
    return `dc446:gapfill-cap:${params.organizationId}:${params.workItemId}:${params.profile}:`;
}
/**
 * Approval progression was blocked by a gate, so downstream generation did not start
 * (varchar(64) on `platform.inbox_items.category`).
 */
export const INBOX_ITEM_CATEGORY_SDD_APPROVAL_PROGRESSION_GATE = 'sdd_approval_progression_gate';
/** Stable origin event prefix for organization access-request inbox rows. */
export const ORGANIZATION_ACCESS_REQUEST_ORIGIN_PREFIX = 'organization_access_request';
export const inboxDemoStageHandoffCategorySchema = z.literal(INBOX_ITEM_CATEGORY_DEMO_STAGE_HANDOFF);
/** Maps top-level inbox tabs to row kind filters (FR-003). */
export const inboxTabSchema = z.enum([
    'all',
    'approvals',
    'pr_findings',
    'risks',
    'mentions',
    'system',
    'done',
]);
export const inboxSeveritySchema = z.enum(['blocking', 'warning', 'info']);
export const inboxItemStatusSchema = z.enum(['open', 'snoozed', 'done']);
export const inboxScopeSchema = z.enum(['me', 'team']);
export const inboxTimeWindowSchema = z.enum(['24h', '7d', '30d', 'all']);
export const primaryActionSchema = z.object({
    label: z.string(),
    href: z.string(),
    method: z.enum(['navigate', 'post']).optional().default('navigate'),
});
export const deliveryLifecycleStateDtoSchema = z.enum([
    'pending',
    'succeeded',
    'failed',
    'exhausted',
    'superseded',
    'cancelled',
    'not_applicable',
]);
export const failureClassDtoSchema = z.enum(['transient', 'permanent', 'unknown']);
export const memberSafeDeliverySummarySchema = z.object({
    milestone_key: z.enum(['spec_ready', 'tech_plan_ready', 'implement_ready', 'review_complete']),
    tracker_state: deliveryLifecycleStateDtoSchema,
    inbox_state: deliveryLifecycleStateDtoSchema,
    tracker_attempt_count: z.number().int().min(0).max(5).optional(),
    first_attempt_at: z.string().datetime().optional(),
    last_attempt_at: z.string().datetime().optional(),
    next_retry_at: z.string().datetime().nullable().optional(),
    failure_class: failureClassDtoSchema.optional(),
    last_http_status: z.number().int().nullable().optional(),
    degraded_message_key: z.string().max(128).optional(),
});
export const inboxItemDtoSchema = z.object({
    id: z.string().uuid(),
    organization_id: z.string().uuid(),
    kind: z.string(),
    category: z.string().nullable(),
    severity: inboxSeveritySchema,
    title: z.string().max(120),
    source_label: z.string(),
    status: inboxItemStatusSchema,
    snoozed_until: z.string().datetime().nullable(),
    primary_action: primaryActionSchema,
    secondary_actions: z.array(z.object({ label: z.string(), href: z.string() })).optional(),
    origin_event_id: z.string(),
    work_item_id: z.string().uuid().nullable(),
    pull_request_id: z.string().uuid().nullable(),
    relative_refreshed_at: z.string().datetime().optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    done_at: z.string().datetime().nullable(),
    /**
     * Arrived after the member's `inbox_previous_seen_at` boundary — the rows the
     * badge was counting when they last walked in. Drives the in-list marker; the
     * list view additionally requires `status === 'open'` so a row the member just
     * actioned stops being highlighted.
     */
    is_new: z.boolean(),
    delivery_summary: memberSafeDeliverySummarySchema.optional(),
    /** DC-373 — present when `implementation_readiness.enabled` is allow and `work_item_id` is set. */
    readiness_snippet: workItemReadinessSnippetSchema.optional(),
});
export const inboxListResponseSchema = z.object({
    items: z.array(inboxItemDtoSchema),
    next_cursor: z.string().nullable(),
});
/**
 * Badge + header counts. `new_count` is deliberately status-blind: an item that
 * arrived and resolved itself while the member was away is still news to them.
 * `open_count` is the backlog figure shown in the page header, never the badge.
 */
export const inboxSummaryResponseSchema = z.object({
    new_count: z.number().int().min(0),
    open_count: z.number().int().min(0),
});
/** Response of the visit stamp. `previous_seen_at` is the new in-list marker boundary. */
export const inboxSeenResponseSchema = z.object({
    previous_seen_at: z.string().datetime().nullable(),
    last_seen_at: z.string().datetime(),
});
function coerceQueryBool(val) {
    if (val === true || val === 'true')
        return true;
    return false;
}
export const inboxListQuerySchema = z
    .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    inbox_tab: inboxTabSchema.optional().default('all'),
    /** DC-388 — optional narrow filter on `platform.inbox_items.category` (e.g. `governance_routing`). */
    category: z.string().min(1).max(64).optional(),
    severity: inboxSeveritySchema.optional(),
    scope: inboxScopeSchema.optional().default('me'),
    time_window: inboxTimeWindowSchema.optional().default('all'),
    include_snoozed: z.preprocess(coerceQueryBool, z.boolean()).optional().default(false),
    include_done: z.preprocess(coerceQueryBool, z.boolean()).optional().default(false),
})
    .superRefine((val, ctx) => {
    if (val.inbox_tab === 'done' && val.include_snoozed) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'include_snoozed is not applicable when inbox_tab is done',
            path: ['include_snoozed'],
        });
    }
});
export function parseInboxListQuery(input) {
    return inboxListQuerySchema.parse(input);
}
export const patchInboxItemBodySchema = z
    .object({
    status: inboxItemStatusSchema,
    snoozed_until: z.string().datetime().optional(),
    spec_clarification_complete: inboxClarificationCompleteBodySchema.optional(),
})
    .superRefine((val, ctx) => {
    if (val.status === 'snoozed' && !val.snoozed_until) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'snoozed_until is required when status is snoozed',
            path: ['snoozed_until'],
        });
    }
    if (val.status !== 'snoozed' && val.snoozed_until !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'snoozed_until must be omitted unless status is snoozed',
            path: ['snoozed_until'],
        });
    }
    if (val.spec_clarification_complete !== undefined && val.status !== 'done') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'spec_clarification_complete is only allowed when status is done',
            path: ['spec_clarification_complete'],
        });
    }
});
function toBase64Url(bytes) {
    if (typeof Buffer !== 'undefined') {
        return Buffer.from(bytes).toString('base64url');
    }
    let bin = '';
    for (let j = 0; j < bytes.length; j++) {
        bin += String.fromCharCode(bytes[j]);
    }
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromBase64Url(s) {
    const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
    if (typeof Buffer !== 'undefined') {
        return new Uint8Array(Buffer.from(b64, 'base64'));
    }
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
export function encodeInboxCursor(updatedAt, id) {
    const payload = { u: updatedAt.getTime(), i: id };
    const json = JSON.stringify(payload);
    return toBase64Url(new TextEncoder().encode(json));
}
export function decodeInboxCursor(cursor) {
    try {
        const bytes = fromBase64Url(cursor);
        const text = new TextDecoder().decode(bytes);
        const parsed = JSON.parse(text);
        const obj = z.object({ u: z.number(), i: z.string().uuid() }).safeParse(parsed);
        return obj.success ? obj.data : null;
    }
    catch {
        return null;
    }
}
/** Maps UI inbox_tab values to persisted kind enum (null = no kind filter). */
export function inboxTabToKind(tab) {
    switch (tab) {
        case 'approvals':
            return 'approval';
        case 'pr_findings':
            return 'pr_finding';
        case 'risks':
            return 'risk';
        case 'mentions':
            return 'mention';
        case 'system':
            return 'system';
        default:
            return null;
    }
}
//# sourceMappingURL=inbox.js.map